import * as t from "io-ts";
import randomSample from "lodash/sample";
import { propertyDescriptionCodec } from "./description";
import { defined } from "./Support";

const themeOverlayCodec = t.keyof({
    none: null,
    dark: null,
    color: null,
    colorDark: null,
    pureblack: null,
    purewhite: null,
});

const logoDisplayCodec = t.keyof({ none: null, logoAndTitle: null, logoOnly: null, titleOnly: null });

const policyLinkCodec = t.type({
    title: propertyDescriptionCodec,
    url: propertyDescriptionCodec,
});

export const baseThemeCodec = t.intersection([
    t.type({
        primaryAccentColor: t.string,
        showTabLabels: t.boolean,
    }),
    t.partial({
        // FIXME: Why is the theme overlay sometimes missing?
        themeOverlay: themeOverlayCodec,
        themeIsAdaptive: t.boolean,
        increaseContrast: t.boolean,
        showDesktopSideBar: t.boolean,

        pageTheme: t.union([t.literal("Accent"), t.literal("Dark"), t.literal("Highlight")]),
        pageBackground: t.union([t.literal("Neutral"), t.literal("Highlight")]),
        pageContainer: t.union([t.literal("Normal"), t.literal("Tight"), t.literal("Narrow")]),

        // FIXME: Type properly
        signInColorTheme: t.string,
        signInBackground: t.string,
        signInBackgroundUploadFileName: t.string,
        signInLogo: t.string,
        signInLogoUploadFileName: t.string,
        signInGreetingText: t.string,
        signInDescriptionText: t.string,
        signInBackgroundOverlay: t.string,
        signInLogoSize: t.string,

        fontTheme: t.string,
        // user agreement props
        requiresAgreement: t.boolean,
        agreementText: propertyDescriptionCodec,
        policyLinks: t.readonlyArray(policyLinkCodec),
        logoDisplay: logoDisplayCodec,
    }),
]);

export type ThemeOverlay = t.TypeOf<typeof themeOverlayCodec>;
export type BaseTheme = t.TypeOf<typeof baseThemeCodec>;
export type LogoDisplay = t.TypeOf<typeof logoDisplayCodec>;

export enum Color {
    Red = "#F44936",
    Orange = "#FC7B01",
    Yellow = "#FFC117",
    LimeGreen = "#7FBA00",
    Jade = "#007E7E",
    Teal = "#02BAAD",
    LightBlue = "#35CFFF",
    Blue = "#29A1FF",
    SkyBlue = "#4574E8",
    NightBlue = "#5754F6",
    Purple = "#9450FC",
    Magenta = "#D63DC2",
    Pink = "#FFA5D6",
    Salmon = "#FD7C84",
    HotPink = "#FF4874",
}

// BaseThemes are the values that get stored and serialized to apps.
// These are not full themes are may contain values that get mapped into a runtime theme.
const baseTheme: BaseTheme = {
    primaryAccentColor: Color.Blue as string, // MUST be a hex value style string #RRGGBB
    showTabLabels: true,
    increaseContrast: false,
    themeOverlay: "none",
    showDesktopSideBar: false,
};

export function randomTheme(themeOverlay: ThemeOverlay): BaseTheme {
    const colors = Object.keys(Color).map(k => Color[k as keyof typeof Color]);
    // let themeOverlays = Object.keys(ThemeOverlay).map(k => ThemeOverlay[k as keyof typeof ThemeOverlay]);
    // if (!getFeatureFlag("colorTheme")) {
    //     themeOverlays = themeOverlays.filter(to => to !== ThemeOverlay.Color);
    // }
    return {
        ...baseTheme,
        primaryAccentColor: defined(randomSample(colors)),
        themeOverlay,
        themeIsAdaptive: true,
    };
}

export const systemFontFamily =
    "Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell, Open Sans, Helvetica Neue, Icons16, sans-serif";
